import { useState, useEffect } from 'react';
import Modal from '../Modal.jsx';
import FormField from '../FormField.jsx';
import Button from '../Button.jsx';
import { useCreateClientInvoice, useUpdateClientInvoice } from '../../hooks/useClientInvoices.js';

const TODAY = new Date().toISOString().slice(0, 10);

function addDaysISO(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function centsToEuros(cents) {
  if (cents == null) return '';
  return String(cents / 100);
}

function eurosToCents(str) {
  const v = parseFloat(str);
  return isNaN(v) ? null : Math.round(v * 100);
}

function defaultForm() {
  return {
    client_name: '',
    label: '',
    amount: '',
    issued_on: TODAY,
    due_on: addDaysISO(TODAY, 30),
    notes: '',
  };
}

function formFromInvoice(invoice) {
  return {
    client_name: invoice.client_name ?? '',
    label: invoice.label ?? '',
    amount: centsToEuros(invoice.amount_cents),
    issued_on: invoice.issued_on ?? TODAY,
    due_on: invoice.due_on ?? addDaysISO(TODAY, 30),
    notes: invoice.notes ?? '',
  };
}

const inputClass = (hasError) =>
  `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors disabled:opacity-50 ${
    hasError ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'
  }`;

/**
 * Modal de création / édition d'une facture client émise.
 *
 * Props :
 *   open      — booléen
 *   onClose   — callback
 *   invoice   — objet facture (mode édition) | null (mode création)
 */
export default function ClientInvoiceFormModal({ open, onClose, invoice = null }) {
  const isEdit = Boolean(invoice);

  const [form, setForm] = useState(() => (isEdit ? formFromInvoice(invoice) : defaultForm()));
  const [errors, setErrors] = useState({});

  const createMut = useCreateClientInvoice();
  const updateMut = useUpdateClientInvoice();
  const mutation = isEdit ? updateMut : createMut;

  // Reset le formulaire à chaque ouverture (création ou changement de facture éditée).
  useEffect(() => {
    if (open) {
      setForm(isEdit ? formFromInvoice(invoice) : defaultForm());
      setErrors({});
      mutation.reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id]);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.client_name.trim()) e.client_name = 'Le client est requis';
    const amountVal = parseFloat(form.amount);
    if (!form.amount || isNaN(amountVal) || amountVal <= 0) {
      e.amount = 'Le montant doit être supérieur à 0';
    }
    if (!form.issued_on) e.issued_on = "La date d'émission est requise";
    if (!form.due_on) e.due_on = "La date d'échéance est requise";
    if (form.issued_on && form.due_on && form.due_on < form.issued_on) {
      e.due_on = "L'échéance doit être postérieure ou égale à l'émission";
    }
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      client_name: form.client_name.trim(),
      label: form.label.trim() || null,
      amount_cents: eurosToCents(form.amount),
      issued_on: form.issued_on,
      due_on: form.due_on,
      notes: form.notes.trim() || null,
    };

    try {
      if (isEdit) {
        await mutation.mutateAsync({ id: invoice.id, ...payload });
      } else {
        await mutation.mutateAsync(payload);
      }
      onClose?.();
    } catch (err) {
      setErrors({ submit: err.message ?? 'Une erreur est survenue' });
    }
  }

  const isPending = mutation.isPending;
  const title = isEdit ? 'Modifier la facture' : 'Nouvelle facture';

  return (
    <Modal open={open} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField label="Client" htmlFor="invoice-client" required error={errors.client_name}>
          <input
            id="invoice-client"
            type="text"
            value={form.client_name}
            onChange={(e) => setField('client_name', e.target.value)}
            placeholder="Nom du client"
            className={inputClass(errors.client_name)}
            disabled={isPending}
          />
        </FormField>

        <FormField label="Description" htmlFor="invoice-label">
          <input
            id="invoice-label"
            type="text"
            value={form.label}
            onChange={(e) => setField('label', e.target.value)}
            placeholder="Ex : Prestation conseil"
            className={inputClass()}
            disabled={isPending}
          />
        </FormField>

        <FormField label="Montant TTC (€)" htmlFor="invoice-amount" required error={errors.amount}>
          <input
            id="invoice-amount"
            type="number"
            min="0.01"
            step="0.01"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => setField('amount', e.target.value)}
            placeholder="0,00"
            className={inputClass(errors.amount)}
            disabled={isPending}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Émise le" htmlFor="invoice-issued" required error={errors.issued_on}>
            <input
              id="invoice-issued"
              type="date"
              value={form.issued_on}
              onChange={(e) => setField('issued_on', e.target.value)}
              className={inputClass(errors.issued_on)}
              disabled={isPending}
            />
          </FormField>

          <FormField label="Échéance" htmlFor="invoice-due" required error={errors.due_on}>
            <input
              id="invoice-due"
              type="date"
              value={form.due_on}
              onChange={(e) => setField('due_on', e.target.value)}
              className={inputClass(errors.due_on)}
              disabled={isPending}
            />
          </FormField>
        </div>

        <FormField label="Notes" htmlFor="invoice-notes">
          <textarea
            id="invoice-notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            rows={2}
            placeholder="Informations complémentaires…"
            className={`${inputClass()} resize-none`}
            disabled={isPending}
          />
        </FormField>

        {errors.submit && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {errors.submit}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending} className="flex-1">
            Annuler
          </Button>
          <Button type="submit" loading={isPending} disabled={isPending} className="flex-1">
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
